// src/features/records/components/ShareDialog/components/RoleSelect.js

import React, { useState, useEffect, useLayoutEffect, useId, useMemo } from "react";
import { ChevronDown, Eye, Pencil, ShieldCheck } from "lucide-react";
import { FloatingPortal } from "@floating-ui/react";
import { useTranslation } from "react-i18next";
import { useFloatingMenu } from "../../../../../hooks/useFloatingMenu";
import styles from "./RoleSelect.module.css";

const ROLES = [
  {
    value: "view",
    labelKey: "records.share.roles.view.label",
    labelDefault: "Can view",
    descriptionKey: "records.share.roles.view.description",
    descriptionDefault: "View and interact with content",
    icon: Eye,
  },
  {
    value: "edit",
    labelKey: "records.share.roles.edit.label",
    labelDefault: "Can edit",
    descriptionKey: "records.share.roles.edit.description",
    descriptionDefault: "Edit content and attachments",
    icon: Pencil,
  },
  {
    value: "admin",
    labelKey: "records.share.roles.admin.label",
    labelDefault: "Admin",
    descriptionKey: "records.share.roles.admin.description",
    descriptionDefault: "Full control and can manage access",
    icon: ShieldCheck,
  },
];

const RoleSelect = ({
  value = "view",
  onChange,
  disabled = false,
  showDescription = true,
  className = "",
  /** "none" | "auto" | "always" — "auto" uses container queries */
  iconMode = "none",
}) => {
  const { t } = useTranslation();
  const roles = useMemo(
    () =>
      ROLES.map((role) => ({
        ...role,
        label: t(role.labelKey, role.labelDefault),
        description: t(role.descriptionKey, role.descriptionDefault),
      })),
    [t]
  );

  const [activeIndex, setActiveIndex] = useState(
    Math.max(
      0,
      roles.findIndex((r) => r.value === value)
    )
  );
  const listboxId = useId();
  const labelId = `${listboxId}-label`;

  const {
    open: isOpen,
    setOpen,
    refs,
    floatingStyles,
    update,
  } = useFloatingMenu({
    placement: "bottom-start",
    sameWidth: true,
    exactWidth: true,     // set exact width
    minWidthPx: 240,      // minimum width floor
  });

  // Recompute activeIndex when value changes from outside
  useEffect(() => {
    setActiveIndex(
      Math.max(
        0,
        roles.findIndex((r) => r.value === value)
      )
    );
  }, [value, roles]);

  // Update position before paint to avoid flash
  useLayoutEffect(() => {
    if (isOpen) {
      update?.();
    }
  }, [isOpen, update]);

  const selectedRole = roles.find((r) => r.value === value) || roles[0];
  const SelectedIcon = selectedRole.icon;

  const handleSelect = (roleValue) => {
    onChange(roleValue);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % roles.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i - 1 + roles.length) % roles.length);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen) {
        handleSelect(roles[activeIndex].value);
      } else {
        setOpen(true);
      }
      return;
    }
    if (e.key === " ") {
      e.preventDefault();
      setOpen((o) => !o);
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Class that toggles icon behavior via CSS
  const iconBehaviorClass =
    iconMode === "always"
      ? styles.iconAlways
      : iconMode === "auto"
      ? styles.iconAuto
      : styles.iconNone;

  return (
    <div className={`${styles.container} ${className} ${iconBehaviorClass}`}>
      <div className={styles.selectWrapper}>
        <button
          type="button"
          ref={refs.setReference}
          className={styles.selectButton}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-labelledby={labelId}
          aria-activedescendant={
            isOpen ? `${listboxId}-opt-${activeIndex}` : undefined
          }
        >
          <div className={styles.selectContent}>
            <div className={styles.selectMainRow}>
              {/* Icon (hidden by default; shown by container query / always) */}
              <SelectedIcon
                className={styles.roleIcon}
                size={16}
                aria-hidden="true"
              />
              <div id={labelId} className={styles.selectLabel}>{selectedRole.label}</div>
            </div>
            {showDescription && (
              <div className={styles.selectDescription}>
                {selectedRole.description}
              </div>
            )}
          </div>
          <ChevronDown
            className={`${styles.selectIcon} ${
              isOpen ? styles.selectIconOpen : ""
            }`}
            size={16}
          />
        </button>

        {isOpen && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              className={`${styles.dropdown} ${styles.dropdownPortal} ${iconBehaviorClass}`}
              style={floatingStyles}
            >
              <div
                id={listboxId}
                role="listbox"
                className={styles.dropdownInner}
              >
                {roles.map((role, idx) => {
                  const Icon = role.icon;
                  return (
                    <button
                      key={role.value}
                      id={`${listboxId}-opt-${idx}`}
                      type="button"
                      className={`${styles.dropdownOption} ${
                        role.value === value ? styles.selected : ""
                      }`}
                      role="option"
                      aria-selected={role.value === value}
                      onClick={() => handleSelect(role.value)}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <Icon
                        className={styles.optionIcon}
                        size={16}
                        aria-hidden="true"
                      />
                      <div className={styles.optionText}>
                        <div className={styles.optionTitle}>{role.label}</div>
                        <div className={styles.optionDescription}>
                          {role.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </FloatingPortal>
        )}
      </div>
    </div>
  );
};

export default RoleSelect;
